import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import './HotelSelector.css'; // We'll create this CSS file next

export default function HotelSelector({ embedded = false }) {
    const [allHotels, setAllHotels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showHotelSelection, setShowHotelSelection] = useState(false);
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { currentUser } = useAuth();
    const [userAccessibleHotels, setUserAccessibleHotels] = useState(null);

    useEffect(() => {
        if (currentUser) {
            setLoading(true);
            currentUser.getIdTokenResult(true)
                .then((idTokenResult) => {
                    const claims = idTokenResult.claims;
                    setUserAccessibleHotels(claims.accessibleHotels || []);
                })
                .catch((err) => {
                    console.error("Error fetching user claims:", err);
                    setError(t('hotelSelector.errorPermissions'));
                    setUserAccessibleHotels([]);
                });
        } else {
            setUserAccessibleHotels([]);
        }
    }, [currentUser, t]);

    useEffect(() => {
        const fetchAllHotels = async () => {
            setError('');
            try {
                const hotelsCollectionRef = collection(db, 'hotels');
                const q = query(hotelsCollectionRef, orderBy('displayName', 'asc'));
                const querySnapshot = await getDocs(q);
                const hotelsList = [];
                querySnapshot.forEach((doc) => {
                    const data = doc.data();
                    if (data.displayName) {
                        hotelsList.push({ id: doc.id, displayName: data.displayName, published: data.published === true });
                    }
                });
                setAllHotels(hotelsList);
            } catch (err) {
                console.error("Error fetching all hotels:", err);
                setError(t('hotelSelector.errorList'));
                setAllHotels([]);
            }
        };

        fetchAllHotels();
    }, [t]);

    const filteredHotels = useMemo(() => {
        if (userAccessibleHotels === null) {
            return [];
        }
        if (!currentUser) {
            // Public visitors only see hotels an admin has explicitly published
            return allHotels.filter(hotel => hotel.published);
        }
        return allHotels.filter(hotel => userAccessibleHotels.includes(hotel.id));
    }, [allHotels, userAccessibleHotels, currentUser]);

    useEffect(() => {
        if (currentUser === undefined) {
            setLoading(true);
        } else if (currentUser && userAccessibleHotels === null) {
            setLoading(true);
        } else {
            setLoading(false);
        }
    }, [currentUser, userAccessibleHotels]);

    const handleHotelSelect = (hotelId) => {
        if (hotelId) {
            if (currentUser) {
                navigate(`/${hotelId}/admin`);
            } else {
                navigate(`/${hotelId}/report-lost-item`);
            }
        }
    };

    const handleReportLostItemClick = () => {
        setShowHotelSelection(true);
    };

    if (loading) {
        return <div className="loading-container">{t('hotelSelector.loading')}</div>;
    }

    if (error) {
        return <div className="error">{error}</div>;
    }

    const noAccess = currentUser && userAccessibleHotels !== null && filteredHotels.length === 0 && allHotels.length > 0;

    const comingSoon = (
        <div className="coming-soon">
            <strong>{t('hotelSelector.comingSoonTitle')}</strong>
            <p>{t('hotelSelector.comingSoonText')}</p>
        </div>
    );

    if (embedded) {
        // Rendered inside the landing page, which supplies its own heading: just the list.
        let emptyMessage = comingSoon;
        if (noAccess) {
            emptyMessage = <p>{t('hotelSelector.noAccessMsg')}</p>;
        } else if (currentUser) {
            emptyMessage = <p>{t('hotelSelector.noHotelsForYou')}</p>;
        }
        return (
            <div className="hotel-selector-container">
                {filteredHotels.length === 0 ? (
                    emptyMessage
                ) : (
                    <ul className="hotel-list">
                        {filteredHotels.map((hotel) => (
                            <li key={hotel.id} className="hotel-list-item" onClick={() => handleHotelSelect(hotel.id)}>
                                {hotel.displayName}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        );
    }

    if (noAccess) {
        return (
            <div className="hotel-selector-container">
                <h2>{t('hotelSelector.selectHotelTitle')}</h2>
                <p>{t('hotelSelector.noAccessMsg')}</p>
            </div>
        );
    }

    if (!currentUser) {
        if (!showHotelSelection) {
            return (
                <div className="hotel-selector-container">
                    <div className="solution-blurb">
                        <h1>{t('hotelSelector.heroTitle')}</h1>
                        <p>{t('hotelSelector.heroText')}</p>
                        <button
                            className="report-lost-item-btn"
                            onClick={handleReportLostItemClick}
                        >
                            {t('hotelSelector.reportButton')}
                        </button>
                    </div>
                </div>
            );
        } else {
            return (
                <div className="hotel-selector-container">
                    <h2>{t('hotelSelector.chooseTitle')}</h2>
                    <p>{t('hotelSelector.chooseText')}</p>
                    {filteredHotels.length === 0 ? (
                        comingSoon
                    ) : (
                        <ul className="hotel-list">
                            {filteredHotels.map((hotel) => (
                                <li key={hotel.id} className="hotel-list-item" onClick={() => handleHotelSelect(hotel.id)}>
                                    {hotel.displayName}
                                </li>
                            ))}
                        </ul>
                    )}
                    <button
                        className="back-btn"
                        onClick={() => setShowHotelSelection(false)}
                    >
                        {t('hotelSelector.back')}
                    </button>
                </div>
            );
        }
    }

    return (
        <div className="hotel-selector-container">
            <h2>{t('hotelSelector.selectHotelTitle')}</h2>
            {filteredHotels.length === 0 ? (
                <p>{t('hotelSelector.noHotelsForYou')}</p>
            ) : (
                <ul className="hotel-list">
                    {filteredHotels.map((hotel) => (
                        <li key={hotel.id} className="hotel-list-item" onClick={() => handleHotelSelect(hotel.id)}>
                            {hotel.displayName}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
